export const categories = [
  { id: "all", label: "All Posts" },
  { id: "architecture", label: "Architecture" },
  { id: "design", label: "Design" },
  { id: "sustainability", label: "Sustainability" },
  { id: "technology", label: "Technology" },
];

export const blogPosts = [
  {
    id: 1,
    slug: "modern-architecture-trends",
    // Photo by Daniel McCullough on Unsplash
    image:
      "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80",
    category: "architecture",
    title: "The Future of Modern Architecture: Sustainable Design Trends",
    description:
      "A deep dive into how real-world sustainable materials and innovative design approaches are shaping the future of architecture, with examples from 2024 projects.",
    author: "Sarah Johnson",
    date: "15 Jan 2024",
    readTime: "5 min read",
    content: {
      sections: [
        {
          title: "The Evolution of Sustainable Architecture",
          content:
            "Sustainable architecture has evolved from a niche concept to a fundamental approach in modern design. Projects like Azur Residence (Costa Rica, 2024) exemplify how buildings can harmonize with their natural surroundings, using local materials and biophilic design. The integration of sustainable practices is now essential for addressing climate change and resource conservation.",
          // Photo by Scott Webb on Unsplash
          image:
            "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Key Sustainable Design Trends",
          content:
            "Key trends include biophilic design (green roofs, living walls), PassivHaus standards for energy efficiency, circular sustainability (recycling materials), and the use of photovoltaic glazing. Real projects like Ka'anche House (Mexico, 2025) and The Edge (Amsterdam) showcase these innovations in action.",
          // Photo by chuttersnap on Unsplash
          image:
            "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Innovative Materials in Modern Architecture",
          content:
            "Materials like cross-laminated timber (CLT), hempcrete, and mycelium composites are revolutionizing construction. The Aqua Pool & Spa (India, 2023) uses sustainable materials to reduce environmental impact while maintaining high performance.",
          // Photo by Ricardo Gomez Angel on Unsplash
          image:
            "https://images.unsplash.com/photo-1468436139062-f60a71c5c892?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "The Role of Technology",
          content:
            "Technologies like Building Information Modeling (BIM), 3D printing, and smart sensors are enabling architects to optimize resources and create more efficient, sustainable buildings. The Edge in Amsterdam is a leading example of AI-powered building management.",
          // Photo by Anders Jildén on Unsplash
          image:
            "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Future Outlook",
          content:
            "The future of sustainable architecture is promising, with a focus on regenerative design and smart city integration. Real-world projects are leading the way in making sustainable design accessible, affordable, and aesthetically compelling.",
          // Photo by Alex Holyoake on Unsplash
          image:
            "https://images.unsplash.com/photo-1466301588506-1a4b7eeea9b0?auto=format&fit=crop&w=800&q=80",
        },
      ],
    },
  },
  {
    id: 2,
    slug: "smart-building-technology",
    // Photo by Expect Best on Pexels
    image:
      "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&w=800",
    category: "technology",
    title: "Smart Buildings: The Integration of AI in Modern Architecture",
    description:
      "How artificial intelligence and real-world smart buildings are revolutionizing building management and energy efficiency.",
    author: "Michael Chen",
    date: "12 Jan 2024",
    readTime: "7 min read",
    content: {
      sections: [
        {
          title: "The Rise of Smart Buildings",
          content:
            "Smart buildings like The Edge (Amsterdam) use AI and IoT to adapt to occupants' needs, optimize energy, and provide comfort. These buildings are intelligent ecosystems, not just structures.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/373912/pexels-photo-373912.jpeg?auto=compress&w=800",
        },
        {
          title: "AI-Powered Building Management",
          content:
            "Modern building management systems leverage AI for predictive analytics, air quality monitoring, automated climate control, predictive maintenance, and advanced security. The Edge is a global benchmark for these technologies.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
        {
          title: "Energy Efficiency and Sustainability",
          content:
            "AI-driven smart buildings achieve energy savings through real-time monitoring, dynamic load balancing, renewable integration, and automated HVAC optimization. The Edge is one of the world's greenest office buildings.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/417273/pexels-photo-417273.jpeg?auto=compress&w=800",
        },
        {
          title: "Enhancing User Experience",
          content:
            "Smart buildings offer personalized environments, mobile app controls, and smart wayfinding. These features improve comfort, productivity, and well-being for occupants.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/373912/pexels-photo-373912.jpeg?auto=compress&w=800",
        },
        {
          title: "Future Developments",
          content:
            "The future includes advanced machine learning, smart city integration, enhanced cybersecurity, and a greater focus on health and wellness in building design.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
  {
    id: 3,
    slug: "sustainable-design-principles",
    // Photo by Tomáš Malík on Pexels
    image:
      "https://images.pexels.com/photos/340140/pexels-photo-340140.jpeg?auto=compress&w=800",
    category: "sustainability",
    title: "Sustainable Design Principles for Modern Architects",
    description:
      "Key principles and real-world practices for creating environmentally conscious architectural designs, with examples from certified projects.",
    author: "Emma Wilson",
    date: "10 Jan 2024",
    readTime: "6 min read",
    content: {
      sections: [
        {
          title: "Understanding Sustainable Design",
          content:
            "Sustainable design in architecture is a holistic approach that considers environmental impact, resource conservation, and human well-being. Ka'anche House (Mexico, 2025) demonstrates how modern architects balance aesthetics with responsibility.",
          // Photo by Binyamin Mellish on Pexels
          image:
            "https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg?auto=compress&w=800",
        },
        {
          title: "Core Principles of Sustainable Architecture",
          content:
            "Principles include energy efficiency, water conservation, sustainable materials, waste reduction, and indoor environmental quality. Certification systems like LEED and Passive House are increasingly adopted worldwide.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/221506/pexels-photo-221506.jpeg?auto=compress&w=800",
        },
        {
          title: "Passive Design Strategies",
          content:
            "Passive design strategies—like building orientation, natural ventilation, and green roofs—are crucial for sustainability. Ka'anche House uses these to optimize comfort and efficiency.",
          // Photo by Scott Webb on Unsplash
          image:
            "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Innovative Sustainable Technologies",
          content:
            "Technologies such as solar PV, geothermal systems, and smart automation are now standard in sustainable projects. These innovations are seen in leading-edge homes and offices globally.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
        {
          title: "Measuring Sustainability",
          content:
            "Sustainability is measured through certifications like LEED, BREEAM, and WELL. These frameworks ensure that projects meet rigorous environmental and health standards.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/221506/pexels-photo-221506.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
  {
    id: 4,
    slug: "interior-design-trends",
    // Photo by Vecislavas Popa on Pexels
    image:
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&w=800",
    category: "design",
    title: "2024 Interior Design Trends: What's Next?",
    description:
      "Discover the latest real-world trends in interior design that are shaping modern living spaces, with examples from award-winning projects.",
    author: "David Brown",
    date: "8 Jan 2024",
    readTime: "4 min read",
    content: {
      sections: [
        {
          title: "The New Era of Interior Design",
          content:
            "2024 marks a shift where functionality meets aesthetic innovation. Zen Villa (Kuwait, 2024) shows how minimalism, comfort, and connection to nature are redefining interiors post-pandemic.",
          // Photo by Max Vakhtbovych on Pexels
          image:
            "https://images.pexels.com/photos/7031400/pexels-photo-7031400.jpeg?auto=compress&w=800",
        },
        {
          title: "Key Design Trends for 2024",
          content:
            "Trends include biophilic design, multifunctional spaces, sustainable materials, smart home integration, and bold colors. Real projects are blending these elements for both style and practicality.",
          // Photo by Max Vakhtbovych on Pexels
          image:
            "https://images.pexels.com/photos/6585761/pexels-photo-6585761.jpeg?auto=compress&w=800",
        },
        {
          title: "Color and Material Palettes",
          content:
            "Earth tones, organic colors, and natural materials dominate 2024's palettes. Zen Villa uses textured surfaces and handcrafted elements to create a warm, inviting atmosphere.",
          // Photo by Max Vakhtbovych on Pexels
          image:
            "https://images.pexels.com/photos/6585763/pexels-photo-6585763.jpeg?auto=compress&w=800",
        },
        {
          title: "Technology Integration",
          content:
            "Smart home technology is seamlessly integrated into modern interiors, with hidden solutions, voice control, and AR for design visualization. Zen Villa incorporates these for a futuristic yet comfortable feel.",
          // Photo by Max Vakhtbovych on Pexels
          image:
            "https://images.pexels.com/photos/6585762/pexels-photo-6585762.jpeg?auto=compress&w=800",
        },
        {
          title: "Future of Interior Design",
          content:
            "The future focuses on personalization, sustainability, wellness, and adaptive layouts. Award-winning projects are leading the way in blending tradition with innovation.",
          // Photo by Max Vakhtbovych on Pexels
          image:
            "https://images.pexels.com/photos/6585764/pexels-photo-6585764.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
  {
    id: 5,
    slug: "green-building-materials",
    // Photo by Scott Webb on Unsplash
    image:
      "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80",
    category: "sustainability",
    title: "Innovative Green Building Materials for Sustainable Construction",
    description:
      "Exploring new materials that are revolutionizing sustainable construction practices, with real project examples.",
    author: "Lisa Anderson",
    date: "5 Jan 2024",
    readTime: "8 min read",
    content: {
      sections: [
        {
          title: "The Green Materials Revolution",
          content:
            "The construction industry is transforming with innovative green materials. Aqua Pool & Spa (India, 2023) uses sustainable alternatives like CLT, recycled plastics, and solar glass to reduce environmental impact.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/221506/pexels-photo-221506.jpeg?auto=compress&w=800",
        },
        {
          title: "Breakthrough Green Materials",
          content:
            "Materials like CLT, hempcrete, mycelium composites, and photovoltaic glass are changing construction. These are now used in both residential and commercial projects worldwide.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
        {
          title: "Benefits and Applications",
          content:
            "Green materials offer reduced carbon footprint, improved energy efficiency, and better indoor air quality. Projects like Aqua Pool & Spa demonstrate their real-world benefits.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/373912/pexels-photo-373912.jpeg?auto=compress&w=800",
        },
        {
          title: "Implementation Challenges",
          content:
            "Challenges include initial costs, supply chain issues, and building code compliance. However, industry adoption is growing as benefits become clear.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/417273/pexels-photo-417273.jpeg?auto=compress&w=800",
        },
        {
          title: "Future of Green Materials",
          content:
            "The future includes advanced bio-based, self-healing, and carbon-capturing materials. More projects are using recycled and upcycled materials for sustainability.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
  {
    id: 6,
    slug: "architectural-visualization",
    // Photo by Daniel McCullough on Unsplash
    image:
      "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80",
    category: "technology",
    title: "The Role of 3D Visualization in Modern Architecture",
    description:
      "How advanced visualization tools are transforming architectural design and presentation, with real project examples.",
    author: "James Wilson",
    date: "3 Jan 2024",
    readTime: "5 min read",
    content: {
      sections: [
        {
          title: "The Evolution of Architectural Visualization",
          content:
            "3D visualization has revolutionized design and presentation. Azur Residence uses photorealistic renderings and VR to communicate ideas and improve planning.",
          // Photo by chuttersnap on Unsplash
          image:
            "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Key Visualization Technologies",
          content:
            "Technologies like real-time rendering, VR, AR, and BIM are now standard. These tools enable immersive design experiences and better client communication.",
          // Photo by Ricardo Gomez Angel on Unsplash
          image:
            "https://images.unsplash.com/photo-1468436139062-f60a71c5c892?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Benefits for Architecture",
          content:
            "3D visualization improves design accuracy, client understanding, and collaboration. Early detection of issues and effective marketing are key benefits.",
          // Photo by Anders Jildén on Unsplash
          image:
            "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Implementation in Practice",
          content:
            "Architects use visualization for design development, presentations, construction planning, and urban development. Azur Residence is a prime example.",
          // Photo by Alex Holyoake on Unsplash
          image:
            "https://images.unsplash.com/photo-1466301588506-1a4b7eeea9b0?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Future Trends",
          content:
            "The future includes AI-powered design, real-time collaboration, and deeper AR/VR integration. Visualization will be central to smart city systems.",
          // Photo by Scott Webb on Unsplash
          image:
            "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80",
        },
      ],
    },
  },
  {
    id: 7,
    slug: "urban-design-challenges",
    // Photo by Pixabay on Pexels
    image:
      "https://images.pexels.com/photos/221506/pexels-photo-221506.jpeg?auto=compress&w=800",
    category: "architecture",
    title: "Addressing Urban Design Challenges in Modern Cities",
    description:
      "Understanding and solving the complex challenges of urban architecture and design, with real project examples.",
    author: "Rachel Martinez",
    date: "1 Jan 2024",
    readTime: "6 min read",
    content: {
      sections: [
        {
          title: "The Urban Design Landscape",
          content:
            "Modern cities face challenges like population growth, climate change, and infrastructure needs. Projects like Ka'anche House (Mexico, 2025) show how urban design can integrate green spaces and community identity.",
          // Photo by Tomáš Malík on Pexels
          image:
            "https://images.pexels.com/photos/340140/pexels-photo-340140.jpeg?auto=compress&w=800",
        },
        {
          title: "Key Urban Challenges",
          content:
            "Challenges include density, climate adaptation, infrastructure modernization, social equity, and sustainable transportation. Mixed-use and green infrastructure are key solutions.",
          // Photo by Binyamin Mellish on Pexels
          image:
            "https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg?auto=compress&w=800",
        },
        {
          title: "Innovative Solutions",
          content:
            "Cities are adopting mixed-use development, smart city tech, and adaptive reuse. Ka'anche House demonstrates how architecture can foster community and sustainability.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/373912/pexels-photo-373912.jpeg?auto=compress&w=800",
        },
        {
          title: "Sustainable Urban Planning",
          content:
            "Sustainable urban design focuses on energy efficiency, renewable integration, water conservation, and green spaces. Real projects are leading the way in these areas.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/210617/pexels-photo-210617.jpeg?auto=compress&w=800",
        },
        {
          title: "Future of Urban Design",
          content:
            "The future emphasizes resilient infrastructure, smart tech, community planning, and climate adaptation. Urban design will be central to sustainable city growth.",
          // Photo by Pixabay on Pexels
          image:
            "https://images.pexels.com/photos/417273/pexels-photo-417273.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
  {
    id: 8,
    slug: "design-thinking-process",
    // Photo by Scott Webb on Unsplash
    image:
      "https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80",
    category: "design",
    title: "The Design Thinking Process in Architecture",
    description:
      "How design thinking methodologies are being applied in architectural projects, with real-world examples.",
    author: "Thomas Lee",
    date: "28 Dec 2023",
    readTime: "7 min read",
    content: {
      sections: [
        {
          title: "Understanding Design Thinking",
          content:
            "Design thinking introduces a human-centered approach to architecture. Zen Villa (Kuwait, 2024) uses participatory design to create spaces that are both functional and meaningful.",
          // Photo by chuttersnap on Unsplash
          image:
            "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "The Design Thinking Process",
          content:
            "The process includes empathizing, defining, ideating, prototyping, and testing. Real projects use workshops, rapid prototyping, and user feedback to refine designs.",
          // Photo by Ricardo Gomez Angel on Unsplash
          image:
            "https://images.unsplash.com/photo-1468436139062-f60a71c5c892?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Application in Architecture",
          content:
            "Design thinking is applied through user research, collaborative workshops, and iterative development. Zen Villa's design process is a model for this approach.",
          // Photo by Anders Jildén on Unsplash
          image:
            "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Benefits for Architectural Practice",
          content:
            "Benefits include better user understanding, more innovative solutions, improved collaboration, and enhanced satisfaction. Real-world feedback is key to success.",
          // Photo by Alex Holyoake on Unsplash
          image:
            "https://images.unsplash.com/photo-1466301588506-1a4b7eeea9b0?auto=format&fit=crop&w=800&q=80",
        },
        {
          title: "Future of Design Thinking",
          content:
            "The future includes digital tools, data-driven decisions, and a focus on sustainability. Design thinking will help architects tackle global challenges.",
          // Photo by Tomáš Malík on Pexels
          image:
            "https://images.pexels.com/photos/340140/pexels-photo-340140.jpeg?auto=compress&w=800",
        },
      ],
    },
  },
];
